import { dashboardApi } from '@/store/api/base/api.base';
import { dropConversationContact, writeConversation, writeConversationContact } from '@/store/api/conversation/email/conversation.cache';
import type { TypeAppDispatch, TypeRootState } from '@/store/store';
import type {
    TypeConversation,
    TypeConversationLimits,
    TypeConversationMessage,
    TypeConversationMoveTarget,
    TypeConversationQuery,
    TypeConversationStatus,
    TypeConversationSummary,
    TypeConversationSyncState,
} from '@/types/conversation/types.conversation.email';
import type {
    TypeConversationContact,
    TypeConversationContactInput,
    TypeConversationContactQuery,
} from '@/types/conversation/types.conversation.email.contact';

type TypeConversationThread = {
    conversation: TypeConversation;
    messages: TypeConversationMessage[];
};

type TypeConversationBody = {
    text: string;
    html: string;
    truncated: boolean;
};

type TypeConversationPage = {
    items: TypeConversation[];
    page: { limit: number; count: number; hasMore: boolean; nextCursor: string | null };
};

type TypeConversationContactPage = {
    items: TypeConversationContact[];
    page: { limit: number; count: number; hasMore: boolean; nextCursor: string | null };
};

const PAGE_SIZE = 50;

const paramsOf = (query: Record<string, unknown>): Record<string, string> =>
    Object.fromEntries(Object.entries(query).filter(([, value]) => value !== undefined && value !== '')) as Record<string, string>;

export const conversationApi = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        listConversations: build.infiniteQuery<TypeConversationPage, TypeConversationQuery, string | null>({
            infiniteQueryOptions: {
                initialPageParam: null,
                getNextPageParam: (last) => (last.page.hasMore && last.page.nextCursor ? last.page.nextCursor : undefined),
            },
            query: ({ queryArg, pageParam }) => ({
                url: '/conversations/email',
                params: { ...paramsOf(queryArg), limit: PAGE_SIZE, ...(pageParam ? { cursor: pageParam } : {}) },
            }),
        }),

        readConversation: build.query<TypeConversationThread, string>({
            query: (id) => ({ url: `/conversations/email/${encodeURIComponent(id)}` }),
            providesTags: (_result, _error, id) => [{ type: 'Conversation', id }],
        }),

        readConversationBody: build.query<TypeConversationBody, { id: string; messageId: string }>({
            query: ({ id, messageId }) => ({ url: `/conversations/email/${encodeURIComponent(id)}/messages/${encodeURIComponent(messageId)}/body` }),
            keepUnusedDataFor: 300,
        }),

        readConversationSummary: build.query<TypeConversationSummary, void>({
            query: () => ({ url: '/conversations/email/summary' }),
        }),

        listConversationContacts: build.infiniteQuery<TypeConversationContactPage, TypeConversationContactQuery, string | null>({
            infiniteQueryOptions: {
                initialPageParam: null,
                getNextPageParam: (last) => (last.page.hasMore && last.page.nextCursor ? last.page.nextCursor : undefined),
            },
            query: ({ queryArg, pageParam }) => ({
                url: '/conversations/email/contacts',
                params: { ...paramsOf(queryArg), limit: PAGE_SIZE, ...(pageParam ? { cursor: pageParam } : {}) },
            }),
        }),

        readConversationContact: build.query<TypeConversationContact, string>({
            query: (id) => ({ url: `/conversations/email/contacts/${encodeURIComponent(id)}` }),
        }),

        markConversationRead: build.mutation<TypeConversation, { id: string; seen: boolean }>({
            query: ({ id, seen }) => ({ url: `/conversations/email/${encodeURIComponent(id)}/read`, method: 'post', data: { seen } }),
            onQueryStarted: async ({ id, seen }, { dispatch, getState, queryFulfilled }) => {
                const undo = patchConversation(dispatch, getState as () => TypeRootState, id, (draft) => {
                    if (seen) draft.counts.unread = 0;
                }, (message) => {
                    if (message.direction === 'in') message.seen = seen;
                });
                try {
                    const { data } = await queryFulfilled;
                    writeConversation(dispatch, data);
                } catch {
                    undo();
                }
            },
        }),

        setConversationStatus: build.mutation<TypeConversation, { id: string; status: TypeConversationStatus }>({
            query: ({ id, status }) => ({ url: `/conversations/email/${encodeURIComponent(id)}/status`, method: 'post', data: { status } }),
            onQueryStarted: async ({ id, status }, { dispatch, getState, queryFulfilled }) => {
                const undo = patchConversation(dispatch, getState as () => TypeRootState, id, (draft) => { draft.status = status; });
                try {
                    const { data } = await queryFulfilled;
                    writeConversation(dispatch, data);
                } catch {
                    undo();
                }
            },
        }),

        assignConversation: build.mutation<TypeConversation, { id: string; assigneeId: string | null }>({
            query: ({ id, assigneeId }) => ({ url: `/conversations/email/${encodeURIComponent(id)}/assign`, method: 'post', data: { assigneeId: assigneeId ?? '' } }),
            onQueryStarted: async ({ id, assigneeId }, { dispatch, getState, queryFulfilled }) => {
                const undo = patchConversation(dispatch, getState as () => TypeRootState, id, (draft) => { draft.assigneeId = assigneeId; });
                try {
                    const { data } = await queryFulfilled;
                    writeConversation(dispatch, data);
                } catch {
                    undo();
                }
            },
        }),

        moveConversation: build.mutation<TypeConversation | null, { id: string; target: TypeConversationMoveTarget }>({
            query: ({ id, target }) => ({ url: `/conversations/email/${encodeURIComponent(id)}/move`, method: 'post', data: { target } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    if (data) writeConversation(dispatch, data);
                } catch {
                }
            },
        }),

        readConversationLimits: build.query<TypeConversationLimits, void>({
            query: () => ({ url: '/conversations/email/limits' }),
            keepUnusedDataFor: 3600,
        }),

        createConversationContact: build.mutation<TypeConversationContact, TypeConversationContactInput>({
            query: (contact) => ({ url: '/conversations/email/contacts', method: 'post', data: { contact } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeConversationContact(dispatch, data);
                } catch {
                }
            },
        }),

        updateConversationContact: build.mutation<TypeConversationContact, { id: string; contact: TypeConversationContactInput }>({
            query: ({ id, contact }) => ({ url: `/conversations/email/contacts/${encodeURIComponent(id)}`, method: 'patch', data: { contact } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeConversationContact(dispatch, data);
                } catch {
                }
            },
        }),

        removeConversationContact: build.mutation<{ id: string }, string>({
            query: (id) => ({ url: `/conversations/email/contacts/${encodeURIComponent(id)}`, method: 'delete' }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    dropConversationContact(dispatch, id);
                } catch {
                }
            },
        }),

        syncConversations: build.mutation<TypeConversationSyncState, void>({
            query: () => ({ url: '/conversations/email/sync', method: 'post' }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(conversationApi.util.updateQueryData('readConversationSummary', undefined, (draft) => { draft.sync = data; }));
                } catch {
                }
            },
        }),
    }),
});

export const cachedLists = (getState: () => TypeRootState): TypeConversationQuery[] =>
    conversationApi.util.selectCachedArgsForQuery(getState(), 'listConversations') as TypeConversationQuery[];

const patchConversation = (
    dispatch: TypeAppDispatch,
    getState: () => TypeRootState,
    id: string,
    apply: (draft: TypeConversation) => void,
    applyMessage?: (draft: TypeConversationMessage) => void,
): (() => void) => {
    const undos: Array<{ undo: () => void }> = [];
    for (const query of cachedLists(getState)) {
        undos.push(dispatch(conversationApi.util.updateQueryData('listConversations', query, (draft) => {
            for (const page of draft.pages) for (const item of page.items) if (item.id === id) apply(item);
        })));
    }
    undos.push(dispatch(conversationApi.util.updateQueryData('readConversation', id, (draft) => {
        apply(draft.conversation);
        if (applyMessage) draft.messages.forEach(applyMessage);
    })));
    return () => undos.forEach((entry) => entry.undo());
};

export const {
    useListConversationsInfiniteQuery, useReadConversationQuery, useReadConversationBodyQuery, useReadConversationSummaryQuery,
    useListConversationContactsInfiniteQuery, useReadConversationContactQuery, useMarkConversationReadMutation, useSetConversationStatusMutation,
    useAssignConversationMutation, useMoveConversationMutation, useSyncConversationsMutation, useReadConversationLimitsQuery,
    useCreateConversationContactMutation, useUpdateConversationContactMutation, useRemoveConversationContactMutation,
} = conversationApi;

export default conversationApi;
